/** Session-scoped memory of the right-dock panel's chat, so a refresh reopens it. */
const ASIDE_THREAD_STORAGE_KEY = 'flora-aside-thread';

/**
 * Page-load-scoped claim on the remembered chat. Refresh recovery only applies
 * to the first panel mount after a page load; later mounts (reopening the
 * panel, changing page) start a new chat. `read` caches the lookup so React
 * Strict Mode's double-invoked state initializer sees the same value; the chat
 * is only marked used (`consumed`) once a panel has actually mounted.
 */
const rememberedThreadOnLoad: { read: boolean; consumed: boolean; threadId: string | undefined } = {
  read: false,
  consumed: false,
  threadId: undefined,
};

/**
 * Reads the right-dock panel's remembered chat, if it belongs to this organization.
 *
 * @param {string | null} organizationId - The current organization
 * @returns {string | undefined} The remembered thread id, or undefined
 */
const readRememberedThread = (organizationId: string | null): string | undefined => {
  try {
    const stored = sessionStorage.getItem(ASIDE_THREAD_STORAGE_KEY);
    if (!stored) return undefined;
    const { orgId, threadId } = JSON.parse(stored) as { orgId?: string; threadId?: string };
    return orgId && orgId === organizationId && threadId ? threadId : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Remembers (or forgets, when `threadId` is undefined) the right-dock panel's chat.
 *
 * @param {string} organizationId - The organization the chat belongs to
 * @param {string | undefined} threadId - The chat to remember
 * @returns {void}
 */
const rememberThread = (organizationId: string, threadId: string | undefined): void => {
  try {
    if (threadId) {
      sessionStorage.setItem(ASIDE_THREAD_STORAGE_KEY, JSON.stringify({ orgId: organizationId, threadId }));
    } else {
      sessionStorage.removeItem(ASIDE_THREAD_STORAGE_KEY);
    }
  } catch {
    /* storage unavailable */
  }
};

/**
 * Returns the chat to restore on the first panel mount after a page load, or
 * undefined once a panel has already mounted this page load. Safe to call
 * repeatedly before the claim is consumed.
 *
 * @param {string | null} organizationId - The current organization
 * @returns {string | undefined} The thread id to restore, or undefined
 */
const peekRememberedThreadOnLoad = (organizationId: string | null): string | undefined => {
  if (rememberedThreadOnLoad.consumed) return undefined;
  // Don't cache a lookup made before the organization resolves.
  if (!rememberedThreadOnLoad.read && organizationId) {
    rememberedThreadOnLoad.read = true;
    rememberedThreadOnLoad.threadId = readRememberedThread(organizationId);
  }
  return rememberedThreadOnLoad.threadId;
};

/**
 * Marks this page load's restore as used, so later panel mounts start fresh.
 *
 * @returns {void}
 */
const consumeRememberedThreadOnLoad = (): void => {
  rememberedThreadOnLoad.consumed = true;
};

/**
 * Resets the page-load claim, as a fresh page load would. Test-only.
 *
 * @returns {void}
 */
const resetRememberedThreadOnLoad = (): void => {
  rememberedThreadOnLoad.read = false;
  rememberedThreadOnLoad.consumed = false;
  rememberedThreadOnLoad.threadId = undefined;
};

export {
  ASIDE_THREAD_STORAGE_KEY,
  consumeRememberedThreadOnLoad,
  peekRememberedThreadOnLoad,
  rememberThread,
  resetRememberedThreadOnLoad,
};
